import React, { useState, useEffect } from "react";
import { Form, Input, DatePicker, Button, message, Upload, InputNumber, Card, Select, Typography } from "antd";
import dayjs from "dayjs";
import axios from "axios";
import SessionManager from "../../auth/sessionmanager";
import { InboxOutlined, DownloadOutlined, PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import ES_SP_01 from "../../data/ES_SP_01.json";
import ES_SP_02 from "../../data/ES_SP_02.json";
import ES_SP_03 from "../../data/ES_SP_03.json";
import ES_SP_06 from "../../data/ES_SP_06.json";
import ES_02 from "../../data/ES_02.json";
import ES_03 from "../../data/ES_03.json";
import ES_04 from "../../data/ES_04.json";
import ES_05 from "../../data/ES_05.json";
import ES_12 from "../../data/ES_12.json";
import ES_13 from "../../data/ES_13.json";
import RouteMap from "./RouteMap";
import { api_forfreight_url } from "../../data/urlData";

const DynamicFormWizard = ({ apiData, pageToNavigate, responseData }) => {
  const templates = {
    ES_SP_01,
    ES_SP_02,
    ES_SP_03,
    ES_SP_06,
    ES_02,
    ES_03,
    ES_04,
    ES_05,
    ES_12,
    ES_13,
  };

  const navigate = useNavigate();
  const { Text } = Typography;
  const [form] = Form.useForm();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isTICJson, setIsTICJson] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [allowedFields, setAllowedFields] = useState([]);
  const [fieldLabels, setFieldLabels] = useState({});

  // Helper function to parse array values from unit field
  const parseArrayFromUnit = (unit) => {
    if (!unit || typeof unit !== "string") return [];

    // Check if unit contains array notation like [value1,value2,value3]
    const arrayMatch = unit.match(/\[([^\]]+)\]/);
    if (arrayMatch) {
      return arrayMatch[1].split(",").map((item) => item.trim());
    }

    return [];
  };

  useEffect(() => {
    if (apiData?.expectedInput) {
      setIsTICJson(
        apiData.expectedInput.some((field) => field.format.toUpperCase() === "JSON" || field.format.includes("Json"))
      );

      // Extract allowed fields from expectedInput
      const fieldsToShow = apiData.expectedInput
        .filter((field) => field.format && field.format.startsWith("ForFreightplatform"))
        .map((field) => field.format);

      // Create a mapping of field paths to their display names
      const labelMapping = {};
      apiData.expectedInput
        .filter((field) => field.format && field.format.startsWith("ForFreightplatform"))
        .forEach((field) => {
          labelMapping[field.format] = field.name;
        });

      setAllowedFields(fieldsToShow);
      setFieldLabels(labelMapping);
    }
  }, [apiData]);

  useEffect(() => {
    if (isTICJson) {
      const initialValues = flattenJson(templates[name]);
      form.setFieldsValue(initialValues);
    }
  }, [isTICJson]);

  useEffect(() => {
    if (
      responseData &&
      (!Array.isArray(responseData) || Array.isArray(responseData)) &&
      Object.keys(responseData).length > 0
    ) {
      setData(responseData);
    }
  }, [responseData]);

  if (!apiData) {
    return <p></p>;
  }
  const { method, basePath, path, expectedInput = [], description, name, id, expectedOutput } = apiData || {};
  const handleSubmit = async (values) => {
    setData(null);
    setLoading(true);
    let token = SessionManager.getToken();

    let sanitizedPath = path ? path : "/";

    let fullUrl = basePath.startsWith("https://")
      ? `${basePath}${sanitizedPath}`
      : `https://${basePath}/${sanitizedPath}`;

    const shouldTrimSlash = (id) =>
      [
        "38a33e04-f605-4e07-949a-fbe8617ab94f",
        "de7e5b1e-51e1-447f-bd03-abcc435f61d3",
        "828042a4-93a3-4f79-9fbc-e0e7aca93a48",
        "42db63df-ff56-4655-9d28-70a487473582",
        "e53d0ee2-63ee-4fe2-944d-97249ae0c8ff",
      ].includes(id);

    let cleanedUrl = shouldTrimSlash(id) ? (fullUrl.endsWith("/") ? fullUrl.slice(0, -1) : fullUrl) : fullUrl;

    // Handle mode parameter in URL path
    let modeValue = null;
    if (values.mode) {
      modeValue = values.mode;
      cleanedUrl = cleanedUrl.replace("{mode}", encodeURIComponent(modeValue));
    }

    let queryParams = {};
    let requestData = {};
    let useFormData = false;
    let formData = new FormData();

    let headers = {
      Authorization: `Bearer ${token}`,
    };

    if (isTICJson) {
      const selectedTemplate = templates[name];
      const updatedJson = applyFormValuesToJson(selectedTemplate, values);
      requestData = updatedJson;
    } else {
      expectedInput.forEach((inputField) => {
        let value = values[inputField.name];
        if (inputField.format?.includes("array")) {
          value = Array.isArray(value) ? value : value ? [value] : [];
        }

        if (
          inputField.format?.includes("%Y-%m-%d") ||
          inputField.unit?.includes("datetime") ||
          inputField.unit?.includes("timestamp")
        ) {
          if (id === "e3f9c443-34c9-47ad-aad7-06fab0288c9b") {
            value = dayjs(value).format("YYYY-MM-DDTHH:mm:ss") + "Z";
          } else {
            value = dayjs(value).format("YYYY-MM-DD HH:mm:ss");
          }
        }

        if (inputField.unit === "YYYY-MM-DD" && inputField.name === "starting_date") {
          value = dayjs(value).format("YYYY-MM-DD");
        }

        if (inputField.type === "float" || inputField.unit === "float") {
          const parsed = parseFloat(value);
          value = isNaN(parsed) ? null : parsed;
        }

        if (inputField.unit?.includes("(x, y, z)")) {
          const raw = value?.replaceAll(".", ",");
          const position = raw?.split(",").map((n) => parseFloat(n.trim()));

          if (position?.length === 3 && !position.some(isNaN)) {
            value = position;
          } else {
            value = null;
          }
        }

        if (method === "GET" && cleanedUrl.includes(`{${inputField.name}}`)) {
          cleanedUrl = cleanedUrl.replace(`{${inputField.name}}`, encodeURIComponent(value));
        }

          // Handle path parameters for ALL methods (not just GET)
          if (inputField.in === "path" && cleanedUrl.includes(`{${inputField.name}}`)) {
            cleanedUrl = cleanedUrl.replace(`{${inputField.name}}`, encodeURIComponent(value));
          }

        const VALID_PATHS = ["/data/trains/{train_id}", "/data/containers/{container_id}"];
        const RO_IDS = [
          "1aa3dbb7-68ac-4b2a-8e62-5336a3dde682",
          "f178caaa-9e32-486f-9eb3-4ae97f923314",
          "8371d496-b503-4361-a04e-648cf2f58dc2",
          "bcb04ff5-e943-49da-9463-8aed29318d56",
        ];

        if (VALID_PATHS.includes(path) && RO_IDS.includes(id)) {
          cleanedUrl = cleanedUrl.replace(`{${inputField.name}}`, encodeURIComponent(value));
        }

        if (inputField.in === "header") {
          headers[inputField.name] = value;
        } else if (inputField.in === "form") {
          useFormData = true;
          console.log(values);  
          if ((Array.isArray(value) && value[0]?.originFileObj instanceof File)) {
            formData.append(inputField.name, value[0].originFileObj);
          }else if (value.file){
             formData.append(inputField.name, value.file);
          }
           else if (value !== undefined && value !== null) {
            formData.append(inputField.name, value);
          }
        } else if (method !== "GET") {
          if (useFormData) {
            formData.append(inputField.name, fileList[0].originFileObj);
          } else {
            requestData[inputField.name] = value;
          }
        }
      });
    }

    const queryString = new URLSearchParams(queryParams).toString();
    if (queryString) {
      cleanedUrl += (cleanedUrl.includes("?") ? "&" : "?") + queryString;
    }

    let finalUrl = cleanedUrl.replace(/([^:]\/)\/+/g, "$1");

    try {
      const response = await axios({
        method: method,
        url: finalUrl,
        headers: useFormData ? headers : headers,
        data: useFormData ? formData : requestData,
      });

      setData(response.data);

      const raw = JSON.stringify({
        username: SessionManager.getUserName(),
        request_datetime: new Date().toISOString(),
        request_service: name,
        request_endpoint: path,
        request_method: method,
        request_body: {
          additionalProp1: {},
        },
        response_status: "200 OK",
        response_body: {
          additionalProp1: response.data,
        },
      });

      const requestOptions = {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: raw,
        redirect: "follow",
      };

      fetch(`${api_forfreight_url}/api/requests`, requestOptions)
        .then((response) => response.text())
        .then((result) => console.log())
        .catch((error) => console.error(error));
    } catch (error) {
      if (error.response) {
        const detail = error.response.data?.detail || error.response.data.message || error.response.data || "API Error";
        message.error(detail);
        console.error("Response error:", error.response.data);
      } else if (error.request) {
        message.error("No response from server.");
        console.error("Request made but no response:", error.request);
      } else {
        message.error("Request setup error: " + error.message);
        console.error("Other error:", error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const renderValue = (value, key = "", level = 0) => {
    const indent = { marginLeft: `${level * 4}px` };
    const formatKey = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

    if (value === null || value === undefined || value === "") {
      return (
        <div style={indent}>
          <strong>{formatKey(key)}</strong>: NULL
        </div>
      );
    }

    if (Array.isArray(value)) {
      return (
        <div style={indent}>
          {key && <strong>{formatKey(key)}</strong>}
          {value.map((item, index) => (
            <div key={index}>{renderValue(item, "", level + 1)}</div>
          ))}
        </div>
      );
    }

    if (typeof value === "object") {
      return (
        <div style={indent}>
          {key && (
            <div>
              <strong>{formatKey(key)}</strong>:
            </div>
          )}
          {Object.entries(value).map(([nestedKey, nestedValue], index) => (
            <div key={index}>{renderValue(nestedValue, nestedKey, level + 1)}</div>
          ))}
        </div>
      );
    }

    if (typeof value === "string") {
      return (
        <div style={indent}>
          <strong>{formatKey(key)}</strong>: {value}
        </div>
      );
    }

    if (dayjs(value, "YYYY-MM-DD HH:mm", true).isValid()) {
      return (
        <div style={indent}>
          <strong>{formatKey(key)}</strong>: {dayjs(value).format("YYYY-MM-DD HH:mm")}
        </div>
      );
    }

    return (
      <div style={indent}>
        <strong>{formatKey(key)}</strong>: {String(value)}
      </div>
    );
  };

  const reset = () => {
    if (pageToNavigate === "WizardSolvingProcesses") {
      const timestamp = new Date().getTime();
      navigate(`/${pageToNavigate}?reset=${timestamp}`);
    } else {
      navigate(`/${pageToNavigate}`);
    }
  };

  const formatLabel = (path) => {
    const parts = path.split(".");

    if (parts[0] === "ForFreightplatform") {
      parts.shift();
    }

    const readableParts = parts.map((part) => {
      part = part.replace(/\[\d+\]/g, "");
      return part.charAt(0).toUpperCase() + part.slice(1);
    });

    return readableParts.join(" → ");
  };

  const getFieldLabel = (pathOrName) => {
    const field = apiData?.expectedInput?.find((f) => f.format === pathOrName || f.name === pathOrName);
    return field?.label || field?.name || formatLabel(pathOrName);
  };

  // Modified createFields function to handle both TIC JSON fields and expectedInput fields
  const createFields = (data, path = "") => {
    const fields = [];
    const renderPath = (key) => (path ? `${path}.${key}` : key);
    const excludedFields = ["ForFreightplatform[0].id", "ForFreightplatform[0].name"];

    // First, handle expectedInput fields (including mode and other fields with arrays)
    if (path === "" && apiData?.expectedInput) {
      apiData.expectedInput.forEach((field) => {
        const { name, unit, format } = field;
        const arrayOptions = parseArrayFromUnit(unit);
        const hasOptions = arrayOptions.length > 0;

        // Handle mode field specifically
        if (name === "mode") {
          fields.push(
            <Form.Item
              key={name}
              label={getFieldLabel(name)}
              name={name}
              rules={[{ required: true, message: `Please select a ${getFieldLabel(name)}` }]}
            >
              {hasOptions ? (
                <Select placeholder={`Select ${getFieldLabel(name)}`}>
                  {arrayOptions.map((option) => (
                    <Select.Option key={option} value={option}>
                      {option}
                    </Select.Option>
                  ))}
                </Select>
              ) : (
                <Input placeholder="Enter mode" />
              )}
            </Form.Item>
          );
          return;
        }

        // Skip TIC 4.0 Json input field as it's handled separately
        if (format === "TIC 4.0 Json") {
          return;
        }

        // Handle other fields with format paths. create a input with empty values.
        // if (format && format.startsWith("ForFreightplatform")) {
        //   fields.push(
        //     <Form.Item
        //       key={name}
        //       label={name}
        //       name={name}
        //       rules={[{ required: false }]}
        //     >
        //       {hasOptions ? (
        //         <Select placeholder={`Select ${name}`}>
        //           {arrayOptions.map(option => (
        //             <Select.Option key={option} value={option}>
        //               {option}
        //             </Select.Option>
        //           ))}
        //         </Select>
        //       ) : unit === 'number' ? (
        //         <InputNumber style={{ width: "100%" }} placeholder={`Enter ${name}`} />
        //       ) : (
        //         <Input placeholder={`Enter ${name}`} />
        //       )}
        //     </Form.Item>
        //   );
        // }
      });
    }

    // Then handle TIC JSON structure if data is provided
    if (!data) return fields;

    if (Array.isArray(data)) {
      if (data.every((item) => typeof item !== "object")) {
        data.forEach((item, index) => {
          const newPath = `${path}[${index}]`;

          // Check if this field is allowed
          if (!allowedFields.includes(newPath)) {
            return;
          }

          // Find corresponding field in expectedInput to check for array options
          const correspondingField = apiData?.expectedInput?.find((field) => field.format === newPath);
          const arrayOptions = correspondingField ? parseArrayFromUnit(correspondingField.unit) : [];
          const hasOptions = arrayOptions.length > 0;

          fields.push(
            <Form.Item
              key={newPath}
              label={getFieldLabel(path)}
              name={newPath}
              rules={[{ required: true, message: "Is required" }]}
            >
              {hasOptions ? (
                <Select placeholder={`Select ${getFieldLabel(path)}`}>
                  {arrayOptions.map((option) => (
                    <Select.Option key={option} value={option}>
                      {option}
                    </Select.Option>
                  ))}
                </Select>
              ) : typeof item === "number" ? (
                <InputNumber style={{ width: "100%" }} placeholder={`Enter number for ${getFieldLabel(path)}`} />
              ) : (
                <Input placeholder={`Enter value for ${getFieldLabel(path)}`} />
              )}
            </Form.Item>
          );
        });
      } else {
        data.forEach((item, index) => {
          fields.push(...createFields(item, `${path}[${index}]`));
        });
      }
    } else if (typeof data === "object" && data !== null) {
      Object.entries(data).forEach(([key, value]) => {
        const newPath = renderPath(key);
        if (excludedFields.includes(newPath)) return;
        fields.push(...createFields(value, newPath));
      });
    } else {
      if (excludedFields.includes(path)) return fields;

      // Check if this field is allowed
      if (!allowedFields.includes(path)) {
        return fields;
      }

      // Find corresponding field in expectedInput to check for array options
      const correspondingField = apiData?.expectedInput?.find((field) => field.format === path);
      const arrayOptions = correspondingField ? parseArrayFromUnit(correspondingField.unit) : [];
      const hasOptions = arrayOptions.length > 0;

      fields.push(
        <Form.Item
          key={path}
          label={getFieldLabel(path)}
          name={path}
          rules={[{ required: true, message: `Is required` }]}
        >
          {hasOptions ? (
            <Select placeholder={`Select ${getFieldLabel(path)}`}>
              {arrayOptions.map((option) => (
                <Select.Option key={option} value={option}>
                  {option}
                </Select.Option>
              ))}
            </Select>
          ) : path.endsWith("timestamp") ? (
            <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: "100%" }} />
          ) : typeof data === "number" ? (
            <InputNumber style={{ width: "100%" }} placeholder={`Enter number for ${getFieldLabel(path)}`} />
          ) : (
            <Input placeholder={`Enter value for ${getFieldLabel(path)}`} />
          )}
        </Form.Item>
      );
    }

    return fields;
  };

  const applyFormValuesToJson = (original, formValues) => {
    const clonedJson = JSON.parse(JSON.stringify(original));

    for (const flatKey in formValues) {
      let value = formValues[flatKey];

      // Convert dayjs objects back to ISO string format
      if (value && typeof value === "object" && value._isADayJSObject) {
        value = value.format("YYYY-MM-DDTHH:mm:ss");
      }

      const keys = flatKey.replace(/\[(\d+)\]/g, ".$1").split(".");

      let current = clonedJson;
      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        if (!(k in current)) current[k] = {};
        current = current[k];
      }

      current[keys[keys.length - 1]] = value;
    }

    return clonedJson;
  };

  const flattenJson = (data, path = "", result = {}) => {
    const renderPath = (key) => (path ? `${path}.${key}` : key);

    if (Array.isArray(data)) {
      data.forEach((item, index) => {
        flattenJson(item, `${path}[${index}]`, result);
      });
    } else if (typeof data === "object" && data !== null) {
      Object.entries(data).forEach(([key, value]) => {
        flattenJson(value, renderPath(key), result);
      });
    } else {
      if (typeof data === "string" && /^\d{4}-\d{2}-\d{2}T/.test(data)) {
        // Use dayjs instead of moment for consistency
        const dayjsObj = dayjs(data);
        result[path] = dayjsObj.isValid() ? dayjsObj : data;
      } else {
        result[path] = data;
      }
    }

    return result;
  };

  const handleExporttxt = () => {
    const fileContent = JSON.stringify(data, null, 4);
    const blob = new Blob([fileContent], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "aircraft_info.txt";
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Helper function to get value from nested object path  (expectedOutput)
  const getValueFromPath = (obj, path) => {
    try {
      let processedPath = path;
      // console.log("Original Path:", path);

      // Handle dynamic array length references (matches array[array.length], array[array.length-1], etc.)
      const lengthPattern = /(\w+)\[(\1)\.length(?:([+-]\d+))?\]/g;

      processedPath = processedPath.replace(lengthPattern, (match, arrayName, _, offset) => {
        // Navigate to the array
        let current = obj;
        const pathBeforeArray = processedPath.substring(0, processedPath.indexOf(match));

        if (pathBeforeArray) {
          const beforePathArray = pathBeforeArray
            .replace(/\[(\d+)\]/g, ".$1")
            .split(".")
            .filter(Boolean);

          for (const key of beforePathArray) {
            if (current === null || current === undefined) break;
            current = current[key];
          }
        }

        const arrayObj = current[arrayName];
        if (!Array.isArray(arrayObj)) {
          // console.warn(`${arrayName} is not an array:`, arrayObj);
          return match;
        }

        const length = arrayObj.length;
        let index;

        // Apply -1 shift to ALL length references
        if (offset) {
          // If offset exists (e.g., length-1, length-2), subtract 1 more
          const offsetValue = parseInt(offset) - 1;
          index = length + offsetValue;
        } else {
          // If no offset (e.g., length), treat as length-1
          index = length - 1;
        }

        // Ensure index is within bounds
        index = Math.max(0, Math.min(index, length - 1));

        return `${arrayName}[${index}]`;
      });

      // Rest of the function remains the same...
      const pathArray = processedPath
        .replace(/\[(\d+)\]/g, ".$1")
        .split(".")
        .filter(Boolean);

      let current = obj;
      for (const key of pathArray) {
        if (current === null || current === undefined) return null;
        current = current[key];
      }

      return current;
    } catch (error) {
      console.warn("Error accessing path:", path, error);
      return null;
    }
  };

  // Enhanced function to find routes by optimal type
  const findRouteByOptimal = (data, optimalType) => {
    try {
      const carriertrips = data?.ForFreightplatform?.[0]?.carriertrip || [];
      return carriertrips.find((trip) => trip.optimal === optimalType);
    } catch (error) {
      console.warn("Error finding route:", error);
      return null;
    }
  };

  // Enhanced function to get specific route data
  const getRouteData = (data, routeType, field) => {
    const route = findRouteByOptimal(data, routeType);
    if (!route) return null;

    switch (field) {
      case "name":
        return route.name;
      case "emissions":
        return route.tripemissions?.[0]?.value;
      case "cost":
        return route.tripcost?.[0]?.value;
      case "duration":
        return route.driving?.duration?.[0]?.value;
      default:
        return null;
    }
  };

  // Function to render TIC JSON data based on expectedOutput (ignoring the "output" line)
  const renderTICJsonData = (data, expectedOutput) => {
    return expectedOutput
      .map((item, index) => {
        // Skip rendering the "output" line entirely
        if (item.name === "output") {
          return null; // This will render nothing for this item
        }

        let value = null;

        // Handle special route-based queries
        if (item.format.includes("optimal=")) {
          // Extract the optimal type and field from the format
          const optimalMatch = item.format.match(/optimal="([^"]+)"/);
          if (optimalMatch) {
            const optimalType = optimalMatch[1];

            // Determine what field to extract
            if (item.format.includes("tripemissions")) {
              value = getRouteData(data, optimalType, "emissions");
            } else if (item.format.includes("tripcost")) {
              value = getRouteData(data, optimalType, "cost");
            } else if (item.format.includes("duration")) {
              value = getRouteData(data, optimalType, "duration");
            } else if (item.format.includes("name")) {
              value = getRouteData(data, optimalType, "name");
            }
          }
        } else {
          // Use the original path-based approach
          value = getValueFromPath(data, item.format);
        }

        // Use label if available, otherwise use name
        const displayName = item.label !== null ? item.label : item.name;

        return (
          <div key={index} style={{ marginBottom: "15px" }}>
            <Text strong style={{ color: "#001529" }}>
              {displayName}:
            </Text>
            <Text style={{ marginLeft: "8px" }}>
              {value !== null && value !== undefined ? (
                <>
                  {typeof value === "number" ? value.toLocaleString() : value.replace(/-/g, " ").replace(/_/g, " ")}
                  {item.unit && item.unit !== "string" && ` ${item.unit}`}
                </>
              ) : (
                "N/A"
              )}
            </Text>
          </div>
        );
      })
      .filter(Boolean); // This removes any null entries from the array
  };

  // Render function for Number of Wagons
  const renderNumberOfWagons = () => {
    return (
      <Form.Item
        label="Number of Wagons"
        name="number_of_wagons"
        style={{ width: "100%" }}
        rules={[{ required: true, message: "Number of wagons is required!" }]}
      >
        <InputNumber style={{ width: "100%" }} placeholder="Enter number of wagons" min={1} />
      </Form.Item>
    );
  };

  const renderContainers = () => {
    return (
      <Form.List name="containers">
        {(fields, { add, remove }) => (
          <>
            <div style={{ marginBottom: 16, marginTop: 16 }}>
              <strong>Containers:</strong>
            </div>
            {fields.map(({ key, name, ...restField }) => (
              <Card
                key={key}
                size="small"
                style={{ marginBottom: 16 }}
                title={`Container ${name + 1}`}
                extra={<MinusCircleOutlined onClick={() => remove(name)} style={{ color: "#ff4d4f" }} />}
              >
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <Form.Item
                    {...restField}
                    label="Container ID"
                    name={[name, "id"]}
                    rules={[{ required: true, message: "Container ID is required!" }]}
                  >
                    <Input placeholder="Enter container ID" />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    label="Weight (kg)"
                    name={[name, "weight"]}
                    rules={[{ required: true, message: "Weight is required!" }]}
                  >
                    <InputNumber style={{ width: "100%" }} placeholder="Enter weight" min={0} />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    label="Size"
                    name={[name, "size"]}
                    rules={[{ required: true, message: "Size is required!" }]}
                  >
                    <InputNumber style={{ width: "100%" }} placeholder="Enter size" min={0} />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    label="Arriving DateTime"
                    name={[name, "arriving_datetime"]}
                    rules={[{ required: true, message: "Arriving datetime is required!" }]}
                    getValueFromEvent={(value) => (value ? value.format("YYYY-MM-DDTHH:mm:ss") : null)}
                    getValueProps={(value) => ({ value: value ? dayjs(value) : null })}
                  >
                    <DatePicker style={{ width: "100%" }} showTime format="YYYY-MM-DD HH:mm:ss" />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    label="Lead Time Date"
                    name={[name, "lead_time_date"]}
                    rules={[{ required: true, message: "Lead time date is required!" }]}
                    style={{ gridColumn: "span 2" }}
                    getValueFromEvent={(value) => (value ? value.format("YYYY-MM-DDTHH:mm:ss") : null)}
                    getValueProps={(value) => ({ value: value ? dayjs(value) : null })}
                  >
                    <DatePicker style={{ width: "100%" }} showTime format="YYYY-MM-DD HH:mm:ss" />
                  </Form.Item>
                </div>
              </Card>
            ))}
            <Form.Item>
              <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                Add Container
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>
    );
  };

  return (
    <Form form={form} onFinish={handleSubmit} layout="vertical">
      {!data && (
        <Card
          style={{
            height: "80vh",
            padding: 20,
            borderRadius: 10,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            backgroundColor: "#ffffff",
            display: "flex",
            flexDirection: "column",
          }}
          styles={{
            body: {
              height: "100%",
              display: "flex",
              flexDirection: "column",
              padding: 20,
            },
          }}
        >
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              marginBottom: 20,
            }}
          >
            <h6 style={{ color: "#001529", textAlign: "left", marginBottom: 20 }}>
              {description} - ({name})
            </h6>

            {!isTICJson &&
              expectedInput.map((field) => {
                if (field.name === "number_of_wagons" && field.unit === "arraynested") {
                  return <div key={field.name}>{renderNumberOfWagons()}</div>;
                }
                if (field.name === "containers" && field.unit === "arraynested") {
                  return <div key={field.name || "containers"}>{renderContainers()}</div>;
                }

                const label = field.name.replace(/_/g, " ").replace(/\b\w/, (c) => c.toUpperCase());
                const isLabelEmpty = !field.label || field.label.trim() === "";
                const labelUnit = field.unit
                  ? field.unit.replace(/_/g, " ").replace(/\b\w/, (c) => c.toUpperCase())
                  : "";
                const isDateTime =
                  field.format?.includes("%Y-%m-%d") ||
                  field.unit?.includes("datetime") ||
                  field.unit?.includes("timestamp") ||
                  field.unit?.includes("YYYY-MM-DD");
                const isFileUpload = field.unit?.includes("xlsx");
                const isFileUploadcsv = field.unit?.includes("csv");
                const isimg = field.unit?.includes("img");
                const isNumber =
                  field.unit === "number" || field.format?.includes("number") || field.format?.includes("integer");
                const isArray = field.format?.includes("array") || field.format?.includes("arrray");
                const ismaxHours = field.unit?.includes("0-23");
                const isTimeShowDisable = field.unit?.includes("YYYY-MM-DD");
                const isfloat = field.format?.includes("float");
                const isZeroToOne = field.unit?.includes("0-1");
                const isXYZ = field.unit?.includes("(x, y, z)");
                const isArrayUnit = field.unit?.startsWith("[") && field.unit?.endsWith("]");
                const arrayUnitValues = isArrayUnit
                  ? field.unit
                      .slice(1, -1)
                      .split(",")
                      .map((v) => v.trim())
                  : [];
                const newlabel = isLabelEmpty ? label : field.label;
                return (
                  <Form.Item
                    key={field.name}
                    label={newlabel}
                    name={field.name}
                    style={{ width: "100%" }}
                    rules={[
                      {
                        required: isArray ? false : true,
                        message: `${newlabel} is required!`,
                      },
                    ]}
                    valuePropName={isFileUpload ? "fileList" : "value"}
                    getValueFromEvent={isFileUpload ? (e) => e.fileList : undefined}
                  >
                    {isFileUpload ? (
                      <Upload
                        accept=".xlsx"
                        beforeUpload={(file) => {
                          if (!file.name.endsWith(".xlsx")) {
                            message.error("Only .xlsx files are allowed!");
                            return Upload.LIST_IGNORE;
                          }
                          return false; // prevent auto upload
                        }}
                        onChange={({ fileList }) => setFileList(fileList)}
                        fileList={fileList}
                      >
                        <div
                          style={{
                            border: "2px dashed #d9d9d9",
                            width: "100%",
                            textAlign: "center",
                            padding: "20px 0",
                          }}
                        >
                          <InboxOutlined style={{ fontSize: 24, color: "#1890ff" }} />
                          <p>Click or drag .xlsx file to upload</p>
                        </div>
                      </Upload>
                    ) : isFileUploadcsv ? (
                      <Upload
                        accept=".csv"
                        beforeUpload={(file) => {
                          if (!file.name.endsWith(".csv")) {
                            message.error("Only .csv files are allowed!");
                            return Upload.LIST_IGNORE;
                          }
                          return false; // prevent auto upload
                        }}
                        onChange={({ fileList }) => setFileList(fileList)}
                        fileList={fileList}
                      >
                        <div
                          style={{
                            border: "2px dashed #d9d9d9",
                            width: "100%",
                            textAlign: "center",
                            padding: "20px 0",
                          }}
                        >
                          <InboxOutlined style={{ fontSize: 24, color: "#1890ff" }} />
                          <p>Click or drag .csv file to upload</p>
                        </div>
                      </Upload>
                    ) : isimg ? (
                      <Upload
                        accept="image/*"
                        beforeUpload={(file) => {
                          // Check if file is an image
                          if (!file.type.startsWith('image/')) {
                            message.error("Only image files are allowed!");
                            return Upload.LIST_IGNORE;
                          }
                          
                          // Optional: Check file size (e.g., 5MB limit)
                          const isLt5M = file.size / 1024 / 1024 < 5;
                          if (!isLt5M) {
                            message.error("Image must be smaller than 5MB!");
                            return Upload.LIST_IGNORE;
                          }
                          
                          return false; // prevent auto upload
                        }}
                        onChange={({ fileList }) => setFileList(fileList)}
                        fileList={fileList}
                        listType="picture" // This shows thumbnails for images
                      >
                        <div
                          style={{
                            border: "2px dashed #d9d9d9",
                            width: "100%",
                            textAlign: "center",
                            padding: "20px 0",
                            borderRadius: "8px",
                          }}
                        >
                          <InboxOutlined style={{ fontSize: 24, color: "#1890ff" }} />
                          <p>Click or drag image to upload</p>
                        </div>
                      </Upload>
                    ) : isDateTime ? (
                      <DatePicker
                        style={{ width: "100%" }}
                        showTime={!isTimeShowDisable}
                        format="YYYY-MM-DD HH:mm:ss"
                      />
                    ) : isNumber ? (
                      <InputNumber
                        style={{ width: "100%" }}
                        placeholder={`Enter ${newlabel}`}
                        max={ismaxHours ? 23 : undefined}
                      />
                    ) : isfloat ? (
                      <InputNumber
                        style={{ width: "100%" }}
                        placeholder={isZeroToOne ? `Enter ${labelUnit}` : `Enter ${newlabel}`}
                        min={0}
                        max={isZeroToOne ? 1 : undefined}
                        step={0.1}
                      />
                    ) : isArray ? (
                      <Select
                        mode="tags"
                        style={{ width: "100%" }}
                        placeholder={`Enter ${newlabel}`}
                        tokenSeparators={[","]}
                      />
                    ) : isArrayUnit ? (
                      <Select style={{ width: "100%" }} placeholder={`Select ${newlabel}`}>
                        {arrayUnitValues.map((val) => (
                          <Select.Option key={val} value={val}>
                            {val}
                          </Select.Option>
                        ))}
                      </Select>
                    ) : isXYZ ? (
                      <Input placeholder="e.g., 12, 34, 56" />
                    ) : (
                      <Input style={{ width: "100%" }} placeholder={`Enter ${newlabel}`} />
                    )}
                  </Form.Item>
                );
              })}

            {isTICJson && <div>{createFields(templates[name])}</div>}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
            <Button type="primary" htmlType="submit" loading={loading} style={{ flex: 1 }}>
              Submit
            </Button>
            <Button onClick={reset} style={{ flex: 1 }}>
              {pageToNavigate === "WizardSolvingProcesses" ? "Clear" : "Back"}
            </Button>
          </div>
        </Card>
      )}
      {data && (
        <Card
          style={{
            height: "80vh",
            padding: 20,
            borderRadius: 10,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            backgroundColor: "#ffffff",
            display: "flex",
            flexDirection: "column",
          }}
          styles={{
            body: {
              height: "100%",
              display: "flex",
              flexDirection: "column",
              padding: 20,
            },
          }}
        >
          <div
            style={{
              flex: 1,
              overflow: "auto",
              marginBottom: 20,
            }}
          >
            <h6 style={{ color: "#001529", textAlign: "left", marginBottom: 20 }}>{description}</h6>
            {(data.coordinates || data.position) && (
              <div style={{ marginBottom: 20 }}>
                <RouteMap coordinates={data.coordinates || data.position} />
              </div>
            )}

            {/* Conditional rendering based on isTICJson */}
            {isTICJson && expectedOutput.length > 1 ? (
              <div>{renderTICJsonData(data, expectedOutput)}</div>
            ) : // Original rendering logic for non-TIC JSON data
            typeof data === "object" && Object.keys(data).length > 0 ? (
              Object.entries(data).map(
                ([key, value]) =>
                  key !== "coordinates" &&
                  key !== "position" && (
                    <div key={key} style={{ marginBottom: "10px" }}>
                      {renderValue(value, key)}
                    </div>
                  )
              )
            ) : (
              <p>{typeof data === "string" ? data : "No data available"}</p>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
            <Button onClick={reset} block style={{ flex: 1 }}>
              {pageToNavigate === "WizardSolvingProcesses" ? "Clear" : "Back"}
            </Button>
            <Button type="primary" icon={<DownloadOutlined />} onClick={handleExporttxt} style={{ flex: 1 }}>
              Export TXT
            </Button>
          </div>
        </Card>
      )}
    </Form>
  );
};

export default DynamicFormWizard;
